import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { css } from '@emotion/react'
import { Button, BorderButton } from '@/components/atoms/button'
import { useAppSelector } from '@/app/hooks'
import { useNavigate } from 'react-router-dom'
import { Loading } from '@/components/organisms/loading'
import { CustomDesignStyle } from '@/components/template/custom-design-style'
import { NgImage, OkImage } from '@/components/atoms/icon'
import { Divider } from '@/components/atoms/divider'
import { GoogleMapCopyLink } from '@/components/organisms/survey/google-map-copy-link'
import { OtherPlatformLink } from '@/components/atoms/other-platform-link'

/**
 * チェックアウト完了ページ
 * チェックアウト処理完了後に表示されるページ
 * アンケートに回答した場合、その結果に応じたメッセージも表示
 */
export const CheckoutThanks: React.FC = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  // Reduxからチェックアウト関連の状態を取得
  const { loading, checkoutResultHistories } = useAppSelector(state => state.checkout.checkoutData)
  const checkoutReservations = useAppSelector(state => state.checkout.checkoutReservation.checkoutReservations)
  // アンケート結果を取得（存在する場合のみ）
  const surveyResult = useAppSelector(state => state.survey.surveyResult.result)

  // 未払いの予約が存在するかチェック
  const existsUnpaidReservation = useMemo(
    () => Object.values(checkoutResultHistories).some(isCompleted => !isCompleted),
    [checkoutResultHistories],
  )

  // フロント対応が必要な予約があるかチェック
  const isFrontSupported = useMemo(() => checkoutReservations?.some(reservation => reservation.frontSupported), [checkoutReservations])

  // スマートキーを使用しているかチェック
  const isUsingSmartKey = useMemo(
    () => checkoutReservations?.length && !checkoutReservations.some(reservation => !reservation.isUsingSmartKey),
    [checkoutReservations],
  )

  const design = useAppSelector(state => state.design)

  // アンケートセクションの区切り線スタイル
  const surveyDividerStyle = useMemo(
    () =>
      css({
        width: '100%',
        border: 'none',
        borderTop: `1px solid ${design?.form.borderColor}`,
        marginTop: '2.5rem',
        marginBottom: '2.5rem',
      }),
    [design],
  )

  return (
    <>
      <CustomDesignStyle />
      <div css={[layoutStyle]}>
        <Loading loading={loading}>
          {!existsUnpaidReservation && !isFrontSupported ? (
            <>
              <OkImage css={imageWrapperStyle} />
              <h2 css={thanksMessageStyle}>{t('Checkout is complete.')}</h2>
              <p css={leadStyle}>
                {t('Thank you for your stay.')}
                <br />
                {t(isUsingSmartKey ? 'Please have a safe trip home.' : 'Please return the key and leave.')}
              </p>
            </>
          ) : (
            <>
              <NgImage css={imageWrapperStyle} />
              <h2 css={thanksMessageStyle}>{t('Please complete the procedure\nat the front desk.')}</h2>
              {!isFrontSupported && (
                <p css={leadStyle}>
                  {t(
                    isUsingSmartKey
                      ? 'Please settle your account at the front desk.'
                      : 'Please return the key and settle your account at the front desk.',
                  )}
                </p>
              )}
            </>
          )}
          {/* アンケート結果が存在する場合、アンケート関連のメッセージを表示 */}
          {surveyResult ? (
            <>
              <Divider customCss={surveyDividerStyle} />
              {!surveyResult.googleMapEnabled ? (
                // 条件1: GoogleMap無効の場合
                <>
                  <h2 css={surveyThanksTitleStyle}>{t('Thank you for your cooperation with the survey.')}</h2>
                  <p css={leadStyle}>{t('We will use your feedback to improve our services.')}</p>
                  <Button text={t('TOP')} onClick={() => navigate('/')} css={buttonStyle} />
                </>
              ) : surveyResult.recommendation === 'recommended' ? (
                // 条件2: 推奨 + GoogleMap有効の場合
                <>
                  <h2 css={surveyThanksTitleStyle}>{t('Thank you for your response to our survey!')}</h2>
                  <p css={leadStyle}>{t('If you don’t mind, we’d greatly appreciate it if you could leave us a review on Google Maps.')}</p>
                  <GoogleMapCopyLink comment={surveyResult.comment || ''} googleMapUrl={surveyResult.googleMapUrl || ''} />
                  <BorderButton text={t('TOP')} onClick={() => navigate('/')} buttonCss={buttonStyle} />
                </>
              ) : surveyResult.recommendation === 'not_recommended' ? (
                // 条件3: 推奨しない + GoogleMap有効の場合
                <>
                  <h2 css={surveyThanksTitleStyle}>{t('Thank you for your response to our survey.')}</h2>
                  <p css={leadStyle}>{t('We sincerely value your feedback and will do our best to make improvements.')}</p>
                  <Button text={t('TOP')} onClick={() => navigate('/')} css={buttonStyle} />
                  <OtherPlatformLink url={surveyResult.googleMapUrl || ''} />
                </>
              ) : (
                // recommendationがnull/undefinedの場合のフォールバック
                <>
                  <h2 css={surveyThanksTitleStyle}>{t('Thank you for your cooperation with the survey.')}</h2>
                  <p css={leadStyle}>{t('We will use your feedback to improve our services.')}</p>
                  <Button text={t('TOP')} onClick={() => navigate('/')} css={buttonStyle} />
                </>
              )}
            </>
          ) : (
            // アンケート結果が存在しない場合(スキップ時)
            <>
              <Button text={t('TOP')} onClick={() => navigate('/')} css={buttonStyle} />
            </>
          )}
        </Loading>
      </div>
    </>
  )
}

const layoutStyle = css({
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '6rem',
  paddingRight: '1.5rem',
  paddingLeft: '1.5rem',
  overflowY: 'auto',
})

const thanksMessageStyle = css({
  fontSize: '22px',
  marginTop: '2.5rem',
  marginBottom: '2rem',
  textAlign: 'center',
  lineHeight: '1.54',
  whiteSpace: 'pre-wrap',
})

const leadStyle = css({
  textAlign: 'center',
  fontSize: '14px',
  lineHeight: '1.4',
  whiteSpace: 'pre-wrap',
})

const imageWrapperStyle = css({
  width: '182px',
  height: '182px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const surveyThanksTitleStyle = css({
  fontSize: '18px',
  marginBottom: '1rem',
  textAlign: 'center',
  lineHeight: '1.54',
  whiteSpace: 'pre-wrap',
})

const buttonStyle = css({
  marginTop: '2.5rem',
  marginBottom: '1.5rem',
})
